import { RESTO_TYPE_EMOJI } from '../constants/restoTypes';

/**
 * Mapping Google Places `primaryType` → emoji.
 * Sert d'avatar visuel pour les concurrents listés via Nearby Search
 * (et pour le bloc "Vos voisins" du Dashboard).
 *
 * Liste fournie par Florian — ne pas altérer sans validation.
 */
export const PRIMARY_TYPE_EMOJI = {
  pizza_restaurant: '🍕',
  seafood_restaurant: '🦞',
  greek_restaurant: '🥙',
  italian_restaurant: '🍝',
  japanese_restaurant: '🍣',
  sushi_restaurant: '🍣',
  mexican_restaurant: '🌮',
  chinese_restaurant: '🥡',
  thai_restaurant: '🍜',
  ramen_restaurant: '🍜',
  indian_restaurant: '🍛',
  french_restaurant: '🥖',
  american_restaurant: '🍔',
  fast_food_restaurant: '🍔',
  hamburger_restaurant: '🍔',
  steak_house: '🥩',
  barbecue_restaurant: '🍖',
  sandwich_shop: '🥪',
  mediterranean_restaurant: '🫒',
  spanish_restaurant: '🥘',
  middle_eastern_restaurant: '🥙',
  turkish_restaurant: '🥙',
  vegan_restaurant: '🥗',
  vegetarian_restaurant: '🥗',
  breakfast_restaurant: '🥞',
  brunch_restaurant: '🥞',
  korean_restaurant: '🍱',
  vietnamese_restaurant: '🍜',
  buffet_restaurant: '🍽️',
  cafe: '☕',
  coffee_shop: '☕',
  bakery: '🥐',
  ice_cream_shop: '🍦',
  bar: '🍷',
  pub: '🍺',
  wine_bar: '🍷',
  bar_and_grill: '🍷',
  tea_house: '🍵',
};

/**
 * Emoji déterministe basé sur le nom du resto (fallback ultime).
 * Hash naïf → toujours le même emoji pour un nom donné, donc stable
 * d'un rendu à l'autre.
 */
export function emojiFor(name = '') {
  const map = ['🍽️','🍷','🐟','🦞','🍕','☕','🥐','🌿','🥗','🍔','🍝','🍣','🥘','🍤','🌮'];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) | 0;
  return map[Math.abs(h) % map.length];
}

/**
 * Heuristique nom → emoji : utilisée quand Google Places ne renvoie pas
 * de `primaryType` exploitable (cas fréquent pour les restos peu indexés).
 *
 * Ordre IMPORTANT : les patterns les plus spécifiques en premier
 * (ex: "pizzeria" doit matcher avant "italien" pour qu'une "pizzeria
 * italienne" donne 🍕 et pas 🍝).
 *
 * Hors scope : cuisines très exotiques (éthiopien, soudanais, etc.) et
 * i18n des patterns. Liste validée par Florian — ne pas étendre sans
 * validation produit.
 */
const NAME_KEYWORDS_EMOJI = [
  { keywords: ['pizza', 'pizzeria'], emoji: '🍕' },
  { keywords: ['coquillag', 'fruits de mer', 'poisson', 'huitres', 'oyster', 'crustac'], emoji: '🦞' },
  { keywords: ['sushi', 'sushiya', 'japonais', 'japonaise'], emoji: '🍣' },
  { keywords: ['thai', 'thaï', 'thaïland', 'thailand'], emoji: '🍜' },
  { keywords: ['vietnamien', 'vietnamienne', 'pho', 'banh mi'], emoji: '🍜' },
  { keywords: ['chinois', 'chinoise', 'wok'], emoji: '🥡' },
  { keywords: ['indien', 'indienne', 'curry', 'tandoori'], emoji: '🍛' },
  { keywords: ['libanais', 'libanaise', 'shawarma', 'falafel'], emoji: '🥙' },
  { keywords: ['arménien', 'arménienne'], emoji: '🥙' },
  { keywords: ['grec', 'grecque', 'souvlaki', 'gyros'], emoji: '🥙' },
  { keywords: ['mexicain', 'mexicaine', 'tacos', 'burrito', 'tex-mex'], emoji: '🌮' },
  { keywords: ['italien', 'italienne', 'pasta', 'trattoria', 'osteria'], emoji: '🍝' },
  { keywords: ['burger', 'smash', 'fast food'], emoji: '🍔' },
  { keywords: ['couscous', 'marocain', 'tajine', 'maghreb'], emoji: '🥘' },
  { keywords: ['tapas', 'espagnol', 'paella'], emoji: '🥘' },
  { keywords: ['bar à vin', 'cave à vin', 'wine bar'], emoji: '🍷' },
  { keywords: ['bistrot', 'brasserie', 'pub'], emoji: '🍻' },
  { keywords: ['crêp', 'crepe', 'galette'], emoji: '🥞' },
  { keywords: ['glacier', 'glace', 'sorbet', 'gelato'], emoji: '🍦' },
  { keywords: ['boulangerie', 'patisserie', 'pâtisserie'], emoji: '🥐' },
  { keywords: ['café', 'coffee'], emoji: '☕' },
  { keywords: ['vegan', 'végétarien', 'salade', 'poke'], emoji: '🥗' },
];

/**
 * Cherche un mot-clé du nom du concurrent dans NAME_KEYWORDS_EMOJI.
 * Retourne l'emoji du PREMIER match (l'ordre des patterns est significatif).
 *
 * @param {string} name  Nom du concurrent (ex: "MERIDIO Cuisine grecque")
 * @returns {string|null} Emoji si match, sinon null.
 */
function matchNameKeyword(name) {
  if (!name) return null;
  const lower = name.toLowerCase();
  for (const { keywords, emoji } of NAME_KEYWORDS_EMOJI) {
    if (keywords.some(k => lower.includes(k))) return emoji;
  }
  return null;
}

/**
 * Choix d'emoji pour un concurrent (ou le resto utilisateur lui-même).
 *
 * Ordre de priorité :
 *  1. Si `competitor.isYou` + `userRestaurant.type_cuisine` connu dans
 *     RESTO_TYPE_EMOJI → emoji local (cohérent avec le profil saisi).
 *  2. `competitor.primary_type` Google mappé dans PRIMARY_TYPE_EMOJI.
 *  2bis. Heuristique nom → emoji (NAME_KEYWORDS_EMOJI) — sert quand
 *        Google ne renvoie pas de primaryType exploitable. N'écrase
 *        JAMAIS un primaryType valide (placé après le niveau 2).
 *  3. Fallback hash du nom (stable).
 *
 * @param {Object} competitor      Concurrent Google Places, shape :
 *                                 `{ name, isYou?, primary_type? }`
 * @param {Object} [userRestaurant] Resto utilisateur, shape :
 *                                 `{ type_cuisine? }`. Optionnel — si absent
 *                                 on retombe directement sur primary_type / hash.
 * @returns {string} Un emoji (jamais vide).
 */
export function pickConcurrentEmoji(competitor, userRestaurant) {
  if (
    competitor?.isYou &&
    userRestaurant?.type_cuisine &&
    RESTO_TYPE_EMOJI[userRestaurant.type_cuisine]
  ) {
    return RESTO_TYPE_EMOJI[userRestaurant.type_cuisine];
  }
  if (competitor?.primary_type && PRIMARY_TYPE_EMOJI[competitor.primary_type]) {
    return PRIMARY_TYPE_EMOJI[competitor.primary_type];
  }
  const fromName = matchNameKeyword(competitor?.name);
  if (fromName) return fromName;
  return emojiFor(competitor?.name);
}
